/*!
 * MAAREE Search
 *
 * Wires up the predictive search panel:
 *   - Open/close (trigger button, backdrop, esc key, /  shortcut)
 *   - Calls /search/suggest.json as the user types, debounced
 *   - Renders products, collections, articles and pages
 *   - Keyboard navigation (Up/Down to step through, Enter to open)
 *   - Recent searches persisted in localStorage
 *   - Highlights matched substring inside product titles
 *   - "See all results" CTA reflects the live count
 *
 * Drives DOM produced by sections/maaree-search-panel.liquid. Every
 * styling decision lives in assets/maaree-search.css; this file only
 * adds/removes attributes and innerHTML.
 */

(function () {
  'use strict';

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Wrap matches of `query` inside `text` with <mark> tags. Case-insensitive.
  function highlight(text, query) {
    if (!query || !text) return escapeHtml(text);
    var safeText = escapeHtml(text);
    var safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      return safeText.replace(new RegExp('(' + safeQuery + ')', 'ig'), '<mark>$1</mark>');
    } catch (_) {
      return safeText;
    }
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme && theme.moneyFormat);
    }
    var amount = (Number(cents) / 100).toFixed(2);
    return '£' + amount;
  }

  // -----------------------------------------------------------------------
  // Recent searches (localStorage)
  // -----------------------------------------------------------------------

  function Recent(key, max) {
    this.key = key || 'maaree-search-recent';
    this.max = max || 4;
  }
  Recent.prototype.list = function () {
    try {
      var raw = localStorage.getItem(this.key);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  };
  Recent.prototype.add = function (q) {
    if (!q) return;
    var arr = this.list().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    arr.unshift(q);
    arr = arr.slice(0, this.max);
    try { localStorage.setItem(this.key, JSON.stringify(arr)); } catch (_) {}
  };
  Recent.prototype.remove = function (q) {
    var arr = this.list().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    try { localStorage.setItem(this.key, JSON.stringify(arr)); } catch (_) {}
  };

  // -----------------------------------------------------------------------
  // Panel controller
  // -----------------------------------------------------------------------

  function MaareeSearch(panelEl) {
    this.panel = panelEl;
    this.root = panelEl.closest('.maaree-search-root') || panelEl.parentElement;
    this.backdrop = $('.maaree-search-backdrop[data-maaree-search-backdrop]', panelEl.parentElement);
    this.body = $('[data-maaree-search-body]', panelEl);
    this.input = $('[data-maaree-search-input]', panelEl);
    this.form = $('[data-maaree-search-form]', panelEl);
    this.clearBtn = $('[data-maaree-search-clear]', panelEl);
    this.closeBtn = $('[data-maaree-search-close]', panelEl);
    this.productsEl = $('[data-maaree-search-products]', panelEl);
    this.productsCount = $('[data-maaree-search-products-count]', panelEl);
    this.noResultsEl = $('[data-maaree-search-noresults]', panelEl);
    this.collectionsEl = $('[data-maaree-search-collections]', panelEl);
    this.collectionsTitle = $('[data-maaree-search-collections-title]', panelEl);
    this.articlesEl = $('[data-maaree-search-articles]', panelEl);
    this.articlesTitle = $('[data-maaree-search-articles-title]', panelEl);
    this.pagesEl = $('[data-maaree-search-pages]', panelEl);
    this.pagesTitle = $('[data-maaree-search-pages-title]', panelEl);
    this.recentEl = $('[data-maaree-search-recent]', panelEl);
    this.recentTitle = $('[data-maaree-search-recent-title]', panelEl);
    this.seeAllBtn = $('[data-maaree-search-see-all]', panelEl);
    this.seeAllText = $('[data-maaree-search-see-all-text]', panelEl);
    this.browseBtn = $('[data-maaree-search-browse]', panelEl);

    this.recent = new Recent(
      panelEl.getAttribute('data-recent-key'),
      parseInt(panelEl.getAttribute('data-recent-max'), 10) || 4
    );
    this.suggestLimit = parseInt(panelEl.getAttribute('data-suggest-limit'), 10) || 5;
    this.searchUrl = (window.routes && window.routes.search_url) || '/search';
    this.suggestUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest.json';
    this.lastQuery = '';
    this.lastAbort = null;
    this.seeAllTpl = this.seeAllText ? this.seeAllText.textContent : 'See all results';

    this.bind();
    this.renderRecent();
  }

  MaareeSearch.prototype.bind = function () {
    var self = this;

    // Trigger buttons (any element with data-maaree-search-open)
    $$('[data-maaree-search-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.open(); });
    });

    // No-touch mode: latch onto the theme's existing search icon so no
    // header.liquid edit is required. We intercept the native search
    // link/button in the capture phase, stop it opening the legacy
    // Maestrooo overlay, and open the MAAREE panel instead.
    var nativeTriggers = $$('.header__search-link a, .header__search-link button, [aria-controls^="header-search"], [aria-controls^="search-drawer"]')
      .filter(function (el) { return !el.hasAttribute('data-maaree-search-open'); });
    nativeTriggers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.open();
      }, true);
      el.setAttribute('aria-haspopup', 'dialog');
    });
    self.nativeTriggers = nativeTriggers;

    // Close handlers
    if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.close(); });
    if (this.backdrop) this.backdrop.addEventListener('click', function () { self.close(); });

    // Clear button
    if (this.clearBtn) this.clearBtn.addEventListener('click', function () {
      self.input.value = '';
      self.form.removeAttribute('data-has-value');
      self.input.focus();
      self.onInput();
    });

    // Input handlers
    if (this.input) {
      this.input.addEventListener('input', debounce(function () { self.onInput(); }, 180));
      this.input.addEventListener('keydown', function (e) { self.onKey(e); });
    }

    // Form submit: save the query then let the browser navigate
    if (this.form) {
      this.form.addEventListener('submit', function () {
        var q = (self.input.value || '').trim();
        if (q) self.recent.add(q);
      });
    }

    // Global keyboard
    document.addEventListener('keydown', function (e) {
      // Esc closes
      if (e.key === 'Escape' && self.isOpen()) {
        e.preventDefault();
        self.close();
        return;
      }
      // "/" focuses the search input (skip when typing in a field)
      if (e.key === '/' && !self.isOpen() && !self.isInField(e.target)) {
        e.preventDefault();
        self.open();
      }
    });
  };

  MaareeSearch.prototype.isInField = function (el) {
    if (!el || !el.tagName) return false;
    var t = el.tagName.toLowerCase();
    if (t === 'input' || t === 'textarea' || t === 'select') return true;
    if (el.isContentEditable) return true;
    return false;
  };

  MaareeSearch.prototype.isOpen = function () {
    return this.panel && !this.panel.hasAttribute('hidden');
  };

  // Measure where the header ends so the fixed panel drops in just beneath it,
  // regardless of announcement bar, sticky header, or theme structure.
  MaareeSearch.prototype.positionBelowHeader = function () {
    if (!this.panel) return;
    var bottom = 0;
    var groups = $$('.shopify-section-group-header-group');
    var self = this;
    if (groups.length) {
      groups.forEach(function (sec) {
        if (sec.contains(self.panel) || (self.root && sec.contains(self.root))) return;
        var r = sec.getBoundingClientRect();
        if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
      });
    }
    if (!bottom) {
      var hdr = document.querySelector('header, .header, [id*="section-header"], [class*="header-wrapper"]');
      if (hdr) bottom = hdr.getBoundingClientRect().bottom;
    }
    if (!bottom || bottom < 0) bottom = 0;
    this.panel.style.setProperty('--m-search-top', Math.round(bottom) + 'px');
  };

  MaareeSearch.prototype.open = function () {
    if (!this.panel) return;
    this.positionBelowHeader();
    this.panel.removeAttribute('hidden');
    this.panel.setAttribute('open', '');
    if (this.backdrop) {
      this.backdrop.removeAttribute('hidden');
      this.backdrop.setAttribute('open', '');
    }
    $$('[data-maaree-search-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
    (this.nativeTriggers || []).forEach(function (el) {
      el.setAttribute('aria-expanded', 'true');
    });
    // Lock background scroll. iOS/Android ignore overflow on <html> alone,
    // so pin the body in place and remember the scroll position.
    this._scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = (-this._scrollY) + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    var self = this;
    setTimeout(function () { if (self.input) self.input.focus(); }, 0);
    this.renderRecent();
  };

  MaareeSearch.prototype.close = function () {
    if (!this.panel) return;
    this.panel.setAttribute('hidden', '');
    this.panel.removeAttribute('open');
    if (this.backdrop) {
      this.backdrop.setAttribute('hidden', '');
      this.backdrop.removeAttribute('open');
    }
    $$('[data-maaree-search-open]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    (this.nativeTriggers || []).forEach(function (el) {
      el.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    if (this._scrollY != null) {
      window.scrollTo(0, this._scrollY);
      this._scrollY = null;
    }
  };

  // ---------- Input handling ----------

  MaareeSearch.prototype.onInput = function () {
    var q = (this.input.value || '').trim();
    if (q.length > 0) {
      this.form.setAttribute('data-has-value', '');
    } else {
      this.form.removeAttribute('data-has-value');
    }

    if (q.length < 2) {
      this.body.setAttribute('data-mode', 'empty');
      this.hideSeeAll();
      this.lastQuery = '';
      return;
    }

    this.body.setAttribute('data-mode', 'typing');
    this.fetchSuggest(q);
  };

  MaareeSearch.prototype.onKey = function (e) {
    var focusables = this.getFocusableResults();
    var currentIdx = focusables.findIndex(function (el) { return el.getAttribute('data-focused') === 'true'; });

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.focusResult(focusables, currentIdx + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.focusResult(focusables, currentIdx - 1);
    } else if (e.key === 'Enter') {
      var current = focusables[currentIdx];
      if (current && current.href) {
        e.preventDefault();
        var q = (this.input.value || '').trim();
        if (q) this.recent.add(q);
        window.location.href = current.href;
      }
      // Otherwise let the form submit naturally (full search)
    }
  };

  MaareeSearch.prototype.getFocusableResults = function () {
    return $$('.maaree-search-product, .maaree-search-suggestion', this.body);
  };

  MaareeSearch.prototype.focusResult = function (list, idx) {
    if (!list.length) return;
    var len = list.length;
    var n = ((idx % len) + len) % len;
    list.forEach(function (el) { el.removeAttribute('data-focused'); });
    list[n].setAttribute('data-focused', 'true');
    if (list[n].scrollIntoView) list[n].scrollIntoView({ block: 'nearest' });
  };

  // ---------- Fetch + render ----------

  MaareeSearch.prototype.fetchSuggest = function (q) {
    var self = this;
    if (this.lastAbort) { try { this.lastAbort.abort(); } catch (_) {} }
    this.lastAbort = new AbortController();
    this.lastQuery = q;

    var params = new URLSearchParams();
    params.set('q', q);
    params.set('section_id', this.panel.getAttribute('data-section-id') || '');
    params.set('resources[type]', 'product,collection,article,page');
    params.set('resources[limit]', String(this.suggestLimit));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku');

    fetch(this.suggestUrl + '?' + params.toString(), {
      signal: this.lastAbort.signal,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (q !== self.lastQuery) return;
        var resources = (data && data.resources && data.resources.results) || {};
        self.renderProducts(resources.products || [], q);
        self.renderCollections(resources.collections || [], q);
        self.renderArticles(resources.articles || [], q);
        self.renderPages(resources.pages || [], q);
        self.updateSeeAll(resources);
      })
      .catch(function () { /* aborted or network error: do nothing */ });
  };

  MaareeSearch.prototype.renderProducts = function (products, q) {
    if (!this.productsEl) return;
    if (!products.length) {
      this.productsEl.innerHTML = '';
      if (this.productsCount) this.productsCount.textContent = '';
      if (this.noResultsEl) this.noResultsEl.removeAttribute('hidden');
      return;
    }
    if (this.noResultsEl) this.noResultsEl.setAttribute('hidden', '');
    if (this.productsCount) this.productsCount.textContent = ' · ' + products.length + ' match' + (products.length === 1 ? '' : 'es');

    var html = products.map(function (p) {
      var imgUrl = p.featured_image ? (p.featured_image.url || p.featured_image) : (p.image || '');
      var imgHtml = imgUrl
        ? '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="72" height="90">'
        : '';
      var priceHtml = '';
      var rawPrice = (p.price !== undefined && p.price !== null && p.price !== '') ? p.price : p.price_min;
      if (rawPrice !== undefined && rawPrice !== null && rawPrice !== '') {
        var cents;
        if (typeof rawPrice === 'number') {
          cents = rawPrice;
        } else {
          var amt = parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''));
          cents = isNaN(amt) ? NaN : Math.round(amt * 100);
        }
        if (!isNaN(cents)) priceHtml = formatMoney(cents);
      }
      var meta = [];
      if (p.product_type) meta.push(escapeHtml(p.product_type));
      if (p.vendor && p.vendor !== 'MAAREE') meta.push(escapeHtml(p.vendor));

      return '' +
        '<a href="' + escapeHtml(p.url) + '" class="maaree-search-product" tabindex="-1">' +
          '<div class="maaree-search-product__thumb">' + imgHtml + '</div>' +
          '<div class="maaree-search-product__body">' +
            '<p class="maaree-search-product__title">' + highlight(p.title, q) + '</p>' +
            (meta.length ? '<div class="maaree-search-product__meta">' + meta.join(' <span class="dot"></span> ') + '</div>' : '') +
          '</div>' +
          (priceHtml ? '<span class="maaree-search-product__price">' + priceHtml + '</span>' : '') +
        '</a>';
    }).join('');
    this.productsEl.innerHTML = html;
  };

  MaareeSearch.prototype.renderSuggestionList = function (items, container, titleEl, kindLabel, iconSvg) {
    if (!container) return;
    if (!items.length) {
      container.innerHTML = '';
      if (titleEl) titleEl.setAttribute('hidden', '');
      return;
    }
    if (titleEl) titleEl.removeAttribute('hidden');
    var html = items.map(function (item) {
      return '' +
        '<a href="' + escapeHtml(item.url) + '" class="maaree-search-suggestion" tabindex="-1">' +
          iconSvg +
          '<span class="maaree-search-suggestion__label">' + escapeHtml(item.title) + '</span>' +
          '<span class="maaree-search-suggestion__kind">' + kindLabel + '</span>' +
        '</a>';
    }).join('');
    container.innerHTML = html;
  };

  MaareeSearch.prototype.renderCollections = function (items) {
    var icon = '<svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M3 7h18M3 12h18M3 17h18" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    this.renderSuggestionList(items, this.collectionsEl, this.collectionsTitle, 'Collection', icon);
  };
  MaareeSearch.prototype.renderArticles = function (items) {
    var icon = '<svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 13h8M8 17h6M8 9h4" stroke-linecap="round"/></svg>';
    this.renderSuggestionList(items, this.articlesEl, this.articlesTitle, 'Article', icon);
  };
  MaareeSearch.prototype.renderPages = function (items) {
    var icon = '<svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    this.renderSuggestionList(items, this.pagesEl, this.pagesTitle, 'Page', icon);
  };

  MaareeSearch.prototype.updateSeeAll = function (resources) {
    if (!this.seeAllBtn) return;
    var products = (resources.products || []).length;
    var collections = (resources.collections || []).length;
    var articles = (resources.articles || []).length;
    var pages = (resources.pages || []).length;
    var total = products + collections + articles + pages;
    if (total === 0) {
      this.hideSeeAll();
      return;
    }
    this.seeAllBtn.removeAttribute('hidden');
    this.seeAllBtn.setAttribute('href', this.searchUrl + '?q=' + encodeURIComponent(this.lastQuery));
    if (this.seeAllText) {
      // Replace "X" placeholder in the schema-provided label with the live count.
      // If the merchant didn't include "X" in the label, append a count suffix.
      var tpl = this.seeAllTpl || 'See all results';
      var label = tpl.indexOf('X') >= 0 ? tpl.replace('X', String(total)) : (tpl + ' (' + total + ')');
      this.seeAllText.textContent = label;
    }
    if (this.browseBtn) this.browseBtn.setAttribute('hidden', '');
  };

  MaareeSearch.prototype.hideSeeAll = function () {
    if (this.seeAllBtn) this.seeAllBtn.setAttribute('hidden', '');
    if (this.browseBtn) this.browseBtn.removeAttribute('hidden');
  };

  // ---------- Recent searches ----------

  MaareeSearch.prototype.renderRecent = function () {
    if (!this.recentEl) return;
    var list = this.recent.list();
    if (!list.length) {
      this.recentEl.innerHTML = '';
      if (this.recentTitle) this.recentTitle.setAttribute('hidden', '');
      return;
    }
    if (this.recentTitle) this.recentTitle.removeAttribute('hidden');
    var self = this;
    var icon = '<svg aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var html = list.map(function (q) {
      var href = self.searchUrl + '?q=' + encodeURIComponent(q);
      return '' +
        '<div class="maaree-search-recent__item">' +
          '<a href="' + escapeHtml(href) + '" class="maaree-search-recent__q">' + icon + escapeHtml(q) + '</a>' +
          '<button type="button" class="maaree-search-recent__remove" data-recent-remove="' + escapeHtml(q) + '" aria-label="Remove">&times;</button>' +
        '</div>';
    }).join('');
    this.recentEl.innerHTML = html;
    $$('[data-recent-remove]', this.recentEl).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.recent.remove(btn.getAttribute('data-recent-remove'));
        self.renderRecent();
      });
    });
  };

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------

  function init() {
    var panel = $('[data-maaree-search-panel]');
    if (!panel) return;
    if (panel.__maareeSearchBound) return;
    panel.__maareeSearchBound = true;
    new MaareeSearch(panel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init when Shopify section editor re-renders the section
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-maaree-search-panel]')) {
      init();
    }
  });
})();

/* ============================================================
   MAAREE Search Results — size / variant fix  (added 2026-06-24, rev2)
   ------------------------------------------------------------
   WHY: the /search results page uses Shopify's standard search
   (the Liquid `search.results` object), which does NOT match
   variant option values such as "34E" or "30GG". The as-you-type
   dropdown uses /search/suggest.json, which DOES match variants.
   Result: sizes that listed in the dropdown returned "No results"
   on the full page after pressing Enter.

   FIX: on the /search page, when the native product results are
   empty, query /search/suggest.json (the same engine the dropdown
   uses) and render the in-stock bras that carry that size into the
   results grid. Word searches that already work are left untouched.

   NOTE: the header search panel ALSO uses class `maaree-search-root`,
   so we anchor off `.maaree-search-grid-wrap__inner` (unique to the
   results section) rather than the first `.maaree-search-root`.
   ============================================================ */
(function () {
  'use strict';

  var SEARCH_URL = (window.routes && window.routes.search_url) || '/search';

  // Only run on the search results page.
  var here = location.pathname.replace(/\/+$/, '');
  var base = String(SEARCH_URL).split('?')[0].replace(/\/+$/, '');
  if (here !== base && !/\/search$/.test(here)) return;

  function onReady(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(amount) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    var n = parseFloat(amount);
    if (isNaN(n)) { return ''; }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
    } catch (e) {
      return cur + ' ' + n.toFixed(2);
    }
  }

  // Normalise a size token for comparison: uppercase, strip spaces.
  function norm(s) { return String(s || '').toUpperCase().replace(/\s+/g, ''); }

  onReady(function () {
    // The results grid container is unique to the /search results section.
    // (The header search panel shares the .maaree-search-root class, so we
    //  must NOT anchor off the first .maaree-search-root.)
    var inner = document.querySelector('.maaree-search-grid-wrap__inner');
    if (!inner) { return; }
    var resultsRoot = inner.closest('.maaree-search-root') || document;

    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) { return; }

    // Did native search already produce product results? The products tab is
    // the first .maaree-search-tab__count within the results section. A number
    // > 0 means native worked, so we leave the page completely alone.
    var nativeProducts = -1;
    var firstTabCount = resultsRoot.querySelector('.maaree-search-tabs .maaree-search-tab__count');
    if (firstTabCount) {
      var parsed = parseInt(firstTabCount.textContent.trim(), 10);
      if (!isNaN(parsed)) { nativeProducts = parsed; }
    }
    if (nativeProducts > 0) { return; }

    // Query the predictive engine (same one the dropdown uses).
    var suggestUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest.json';
    var qp = new URLSearchParams();
    qp.set('q', q);
    qp.set('resources[type]', 'product');
    qp.set('resources[limit]', '10');
    qp.set('resources[options][unavailable_products]', 'last');

    fetch(suggestUrl + '?' + qp.toString(), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) { return; }
        var products = (((data.resources || {}).results || {}).products) || [];
        if (!products.length) { return; }

        // Keep only products whose matching variant for this size is in stock.
        var qn = norm(q);
        var inStock = products.filter(function (p) {
          var vs = p.variants || [];
          var sized = vs.filter(function (v) { return norm(v.title).indexOf(qn) !== -1; });
          if (sized.length) { return sized.some(function (v) { return v.available; }); }
          return !!p.available;
        });
        if (!inStock.length) { return; }

        // Build cards mirroring snippets/maaree-search-product-card.liquid markup.
        var cards = inStock.map(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image
                  : (typeof p.image === 'string' ? { url: p.image, alt: p.title } : null);
          var media = img && img.url
            ? '<div class="maaree-search-card__media"><img src="' + esc(img.url) +
              '" alt="' + esc(img.alt || p.title) + '" loading="lazy"></div>'
            : '<div class="maaree-search-card__media"></div>';
          return '<a href="' + esc(p.url) + '" class="maaree-search-card">' +
                   media +
                   '<p class="maaree-search-card__title">' + esc(p.title) + '</p>' +
                   '<p class="maaree-search-card__price">' + esc(formatMoney(p.price)) + '</p>' +
                 '</a>';
        }).join('');

        var block =
          '<div class="maaree-search-grid-head" data-maaree-size-results>' +
            '<div class="maaree-search-grid-head__copy">' +
              '<p class="eyebrow">In stock</p>' +
              '<h3>Bras available in ' + esc(q) + '</h3>' +
            '</div>' +
          '</div>' +
          '<div class="maaree-search-grid">' + cards + '</div>';

        // Swap the no-results content for the in-stock grid.
        inner.innerHTML = block;

        // Flip the hero from "No results for X" to "Results for X".
        var h1 = resultsRoot.querySelector('.maaree-search-hero h1');
        if (h1) { h1.innerHTML = 'Results for <span class="q">' + esc(q) + '</span>'; }
      })
      .catch(function () { /* fail quietly: leave the native page as-is */ });
  });
})();